// Live per-retailer health, measured the same way the Discord report measures
// it: did products actually reach users today. This is independent of the
// daily S3 analytics snapshot, which is written once each morning and so
// reflects whatever the fleet looked like at that single instant.

import FetchClient, { type ApiResponse } from "./FetchClient";

export type RetailerHealthStatus = "healthy" | "waiting" | "behind" | "stale";

export interface RetailerHealthEntry {
  slug: string;
  displayName: string;
  kind: "in_store" | "online";
  status: RetailerHealthStatus;
  storesWithProducts: number;
  storesTotal: number;
  coverage: number;
  products: number;
  newestUpdate?: number;
  error?: string;
}

export interface RetailerHealthData {
  generatedAt: number;
  dayStart: number;
  timezone: string;
  threshold: number;
  retailers: RetailerHealthEntry[];
}

export async function getRetailerHealth(): Promise<RetailerHealthData> {
  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  const response = await FetchClient.get<ApiResponse<RetailerHealthData>>(
    `/admin/retailer-health?timezone=${encodeURIComponent(timezone)}`
  );
  if (!response?.data) {
    throw new Error("Retailer health returned no data");
  }
  return response.data;
}

/** Human summary for one retailer, mirroring the Discord line. */
export function describeRetailer(entry: RetailerHealthEntry): string {
  if (entry.error) return `check failed: ${entry.error}`;
  if (entry.kind === "online") {
    if (entry.status === "waiting") return "not due yet";
    if (entry.status === "stale") return "no products for days";
    return `${entry.products.toLocaleString()} products today`;
  }
  const base = `${entry.storesWithProducts}/${entry.storesTotal} stores with products today`;
  return entry.status === "waiting" ? `${base} · still crawling` : base;
}
